import type { Sport, SeriesFormat } from '@/types/api'
import { SPORT_ICONS, type IconName } from './icons'

export const DEFAULT_SPORT: Sport = 'SPORT_TABLE_TENNIS'
export const SUPPORTED_SPORTS: Sport[] = [
  DEFAULT_SPORT,
  'SPORT_TENNIS',
  'SPORT_PADEL',
  'SPORT_BADMINTON',
  'SPORT_SQUASH',
  'SPORT_PICKLEBALL',
  'SPORT_CHESS',
  'SPORT_TABLE_HOCKEY',
  'SPORT_FOOSBALL'
]

/**
 * Returns sports sorted alphabetically by their translated names.
 * @param sports - Array of sports to sort
 * @param t - Translation function from i18next
 */
export function getSortedSports(sports: Sport[], t: (key: string) => string): Sport[] {
  return [...sports].sort((a, b) => {
    const nameA = t(sportTranslationKey(a))
    const nameB = t(sportTranslationKey(b))
    return nameA.localeCompare(nameB, undefined, { sensitivity: 'base' })
  })
}

export const DEFAULT_SERIES_FORMAT: SeriesFormat = 'SERIES_FORMAT_OPEN_PLAY'
export const SUPPORTED_SERIES_FORMATS: SeriesFormat[] = [
  DEFAULT_SERIES_FORMAT,
  'SERIES_FORMAT_LADDER',
  'SERIES_FORMAT_CUP',
  'SERIES_FORMAT_GROUP_KNOCKOUT'
]

/**
 * Returns the i18n translation key for a given sport.
 * Keys should match those defined in the i18n locale files.
 */
export function sportTranslationKey(sport: Sport): string {
  switch (sport) {
    case 'SPORT_TABLE_TENNIS':
      return 'sports.table_tennis'
    case 'SPORT_TENNIS':
      return 'sports.tennis'
    case 'SPORT_PADEL':
      return 'sports.padel'
    case 'SPORT_BADMINTON':
      return 'sports.badminton'
    case 'SPORT_SQUASH':
      return 'sports.squash'
    case 'SPORT_PICKLEBALL':
      return 'sports.pickleball'
    case 'SPORT_CHESS':
      return 'sports.chess'
    case 'SPORT_TABLE_HOCKEY':
      return 'sports.table_hockey'
    case 'SPORT_FOOSBALL':
      return 'sports.foosball'
    default:
      return 'sports.unknown'
  }
}

/**
 * Returns the Iconify icon name for a given sport.
 * Uses proper sport equipment icons from Material Design Icons and Game Icons.
 */
export function sportIcon(sport: Sport): IconName {
  switch (sport) {
    case 'SPORT_TABLE_TENNIS':
      return SPORT_ICONS.tableTennis  // Ping pong paddle
    case 'SPORT_TENNIS':
      return SPORT_ICONS.tennis       // Tennis racket
    case 'SPORT_PADEL':
      return SPORT_ICONS.padel        // Padel racket
    case 'SPORT_BADMINTON':
      return SPORT_ICONS.badminton    // Badminton racket
    case 'SPORT_SQUASH':
      return SPORT_ICONS.squash       // Squash racket
    case 'SPORT_PICKLEBALL':
      return SPORT_ICONS.pickleball   // Pickleball paddle
    case 'SPORT_RACQUETBALL':
      return SPORT_ICONS.racquetball  // Racquetball racket
    case 'SPORT_BEACH_TENNIS':
      return SPORT_ICONS.beachTennis  // Beach tennis racket
    case 'SPORT_CHESS':
      return SPORT_ICONS.chess        // Chess knight
    case 'SPORT_TABLE_HOCKEY':
      return SPORT_ICONS.tableHockey  // Table hockey sticks
    case 'SPORT_FOOSBALL':
      return SPORT_ICONS.foosball     // Foosball (table football)
    default:
      return SPORT_ICONS.tableTennis
  }
}

export function seriesFormatTranslationKey(format: SeriesFormat): string {
  switch (format) {
    case 'SERIES_FORMAT_OPEN_PLAY':
      return 'series.formatOptions.open_play'
    case 'SERIES_FORMAT_LADDER':
      return 'series.formatOptions.ladder'
    case 'SERIES_FORMAT_CUP':
      return 'series.formatOptions.cup'
    case 'SERIES_FORMAT_GROUP_KNOCKOUT':
      return 'series.formatOptions.group_knockout'
    default:
      return 'series.formatOptions.unknown'
  }
}
